import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { get } from "../../redux/ApiHelper";
import { ParentInterface } from "../../interfaces/parent.interface";
import { AccountVerificationStatus } from "../../interfaces/verification.interface";
import { DispatchType } from "../../interfaces/redux.interface";
import { startLoading, stopLoading } from "../../redux/slices/WindowSlice";
import { RootState } from "../../redux/store";
import { errorToast } from "../../utils/toast.util";
import VerificationStepsPanel from "../../components/verification/VerificationStepsPanel";
import { Student } from "../../interfaces/student.interface";
import { PinLocationInterface } from "../../interfaces/pin-location.interface";
import {
  buildAdminMapViewUrl,
  getCoordinatesFromPinLocation,
} from "../../utils/olaMap.util";
import "./ParentDetails.scss";

const formatJoinedDate = (value?: string) =>
  value
    ? new Date(value).toLocaleDateString("en-IN", { dateStyle: "medium" })
    : "N/A";

const ParentDetails = () => {
  const dispatch: DispatchType = useDispatch();
  const { signedAdmin } = useSelector((state: RootState) => state.admin);
  const { id } = useParams<{ id: string }>();
  const [parent, setParent] = useState<ParentInterface | null>(null);
  const [verification, setVerification] =
    useState<AccountVerificationStatus | null>(null);
  const [pinLocation, setPinLocation] = useState<PinLocationInterface | null>(
    null
  );

  const hidePassword =
    signedAdmin.type === "franchise" ||
    (signedAdmin.type === "employee" && signedAdmin.role === "admin");

  useEffect(() => {
    if (!id) return;
    dispatch(startLoading());
    Promise.all([
      get(`/admin/parent/${id}`),
      get(`/admin/parent/${id}/verification`),
      get(`/admin/parent/${id}/pin-location`, {}, {
        suppressNotFoundToast: true,
      }).catch(() => null),
    ])
      .then(([profileRes, verificationRes, pinLocationRes]) => {
        setParent(profileRes);
        setVerification(verificationRes);
        setPinLocation(pinLocationRes?.pinLocation ?? null);
      })
      .catch(() => {
        errorToast("Failed to load parent details");
      })
      .finally(() => {
        dispatch(stopLoading());
      });
  }, [id, dispatch]);

  if (!parent) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="text-xl font-semibold text-gray-700">Loading...</div>
      </div>
    );
  }

  const address = parent.address;
  const pinMapCoords = getCoordinatesFromPinLocation(pinLocation);

  return (
    <div className="flex flex-col p-4 parent-details">
      <div className="flex flex-col rounded-lg shadow-lg">
        <div className="flex flex-col md:flex-row items-center md:items-start gap-8 p-4 h-fit">
          {parent.profilePicture ? (
            <img
              src={parent.profilePicture}
              alt={`${parent.name}'s profile`}
              className="w-32 h-32 rounded-full shadow-md object-cover border-4 border-yellow-500"
            />
          ) : (
            <div className="w-32 h-32 rounded-full bg-gray-700 border-4 border-yellow-500 flex items-center justify-center text-gray-400 text-sm">
              No photo
            </div>
          )}
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-yellow-500 text-left">
              {parent.name || "Unnamed Parent"}
            </h1>
            <p className="text-gray-300 mt-2 text-left">
              {parent.email || "No email"} · +{parent.phoneCode || "91"}{" "}
              {parent.phoneNumber}
            </p>
            <p className="text-gray-400 text-sm mt-1 text-left">
              Joined: {formatJoinedDate(parent.createdAt)}
            </p>
            <div className="flex flex-wrap gap-2 mt-3">
              <span
                className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold ${
                  parent.isAccountVerified
                    ? "bg-green-500/20 text-green-300 border border-green-400/40"
                    : "bg-amber-500/20 text-amber-300 border border-amber-400/40"
                }`}
              >
                {parent.isAccountVerified ? "Account Verified" : "Not Verified"}
              </span>
              <span
                className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold ${
                  parent.isActive?.value !== false
                    ? "bg-green-500/20 text-green-300 border border-green-400/40"
                    : "bg-red-500/20 text-red-300 border border-red-400/40"
                }`}
              >
                {parent.isActive?.label || (parent.isActive?.value !== false ? "Active" : "Inactive")}
              </span>
            </div>
          </div>
        </div>

        <div className="px-4 pb-4">
          <VerificationStepsPanel
            verification={verification}
            renderStepExtra={(step) => {
              if (step.key === "setAddress" && pinMapCoords) {
                return (
                  <div className="mt-3 border-t border-gray-700 pt-3">
                    <button
                      type="button"
                      onClick={() =>
                        window.open(
                          buildAdminMapViewUrl(
                            pinMapCoords.lat,
                            pinMapCoords.lng,
                            14,
                            pinLocation?.formattedAddress
                          ),
                          "_blank",
                          "noopener,noreferrer"
                        )
                      }
                      className="inline-flex items-center px-3 py-1.5 rounded-md text-xs font-medium bg-amber-500/20 text-amber-300 border border-amber-400/40 hover:bg-amber-500/30 transition"
                    >
                      View on Map
                    </button>
                  </div>
                );
              }
              return null;
            }}
          />
        </div>

        <div className="md:flex p-4 gap-6">
          <div className="w-full md:w-1/2 text-left space-y-6">
            <section>
              <h2 className="text-xl font-semibold text-yellow-500">Profile</h2>
              <div className="mt-4 space-y-2 text-gray-200 text-sm">
                <p>
                  <span className="font-semibold text-yellow-400">Email:</span>{" "}
                  {parent.email || "N/A"}
                </p>
                <p>
                  <span className="font-semibold text-yellow-400">Phone:</span>{" "}
                  +{parent.phoneCode || "91"} {parent.phoneNumber}
                </p>
                {!hidePassword && (
                  <p>
                    <span className="font-semibold text-yellow-400">Password:</span>{" "}
                    {parent.password || "N/A"}
                  </p>
                )}
                <p>
                  <span className="font-semibold text-yellow-400">Email Verified:</span>{" "}
                  {parent.isMailVerified ? "Yes" : "No"}
                </p>
                <p>
                  <span className="font-semibold text-yellow-400">Phone Verified:</span>{" "}
                  {parent.isPhoneNumberVerified ? "Yes" : "No"}
                </p>
                <p>
                  <span className="font-semibold text-yellow-400">Coins:</span>{" "}
                  {parent.coins ?? 0}
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-yellow-500">Address</h2>
              <div className="mt-4 text-gray-200 text-sm">
                {address ? (
                  <p>
                    {address.addressLine}, {address.city}, {address.state}
                    {address.pinCode ? ` - ${address.pinCode}` : ""}
                  </p>
                ) : (
                  <p className="text-gray-400">No address on file.</p>
                )}
              </div>
            </section>
          </div>

          <div className="w-full md:w-1/2 text-left space-y-6">
            <section>
              <h2 className="text-xl font-semibold text-yellow-500">Students</h2>
              <div className="mt-4 grid grid-cols-1 gap-3">
                {parent.students?.length ? (
                  parent.students.map((student: Student) => (
                    <div
                      key={student._id}
                      className="rounded-xl border border-gray-600 bg-gray-800/60 p-3 text-sm text-gray-200"
                    >
                      <p className="font-semibold text-yellow-400">{student.name}</p>
                      <p>Class: {student.class || "N/A"}</p>
                      <p>Board: {student.board || "N/A"}</p>
                      <p>Institute: {student.institute || "N/A"}</p>
                      <p>Gender: {student.gender || "N/A"}</p>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-400 text-sm">No students added.</p>
                )}
              </div>
            </section>

            {parent.admin && (
              <section>
                <h2 className="text-xl font-semibold text-yellow-500">
                  Assigned Admin
                </h2>
                <div className="mt-4 space-y-2 text-gray-200 text-sm">
                  <p>
                    <span className="font-semibold text-yellow-400">Name:</span>{" "}
                    {parent.admin.name}
                  </p>
                  <p>
                    <span className="font-semibold text-yellow-400">Email:</span>{" "}
                    {parent.admin.email}
                  </p>
                  <p>
                    <span className="font-semibold text-yellow-400">Phone:</span>{" "}
                    {parent.admin.phoneNumber}
                  </p>
                </div>
              </section>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ParentDetails;
